import { GeckoComponentModelType } from 'types'

import {
  LABEL_BACKGROUND,
  LABEL_EDGE_PADDING_PX,
  LABEL_TEXT_COLOR,
} from './constants'
import { buildReportOverlayPrimitives } from './overlayPrimitives'
import type {
  LabelBox,
  NormalizedBounds,
  OverlayLabelAnchor,
  OverlayLabelPrimitive,
  OverlayShapePrimitive,
  PlotMetrics,
  PositionedLabel,
  PositionedTextLabel,
  ReportHeatmapRequest,
} from './types'
import {
  OverlayLabelAnchor as LabelAnchor,
  OverlayLabelPlacement as LabelPlacement,
  ReportLabelMode as LabelMode,
  ReportOverlayMode as OverlayMode,
} from './types'
import { clamp, measureCanvasTextWidth, roundToDecimal } from './utils'

export function getRightGutterWidth(
  ctx: CanvasRenderingContext2D,
  request: Pick<
    ReportHeatmapRequest,
    | 'componentModel'
    | 'componentOverlayMode'
    | 'includeComponentOverlay'
    | 'includeOverlay'
    | 'labelMode'
    | 'overlayMode'
    | 'overlayPrimitives'
    | 'nominalDisplayUnit'
  >,
  bounds: NormalizedBounds
) {
  if (!shouldReserveRightGutter(request)) {
    return 0
  }

  const primitives = buildReportOverlayPrimitives(request, bounds)
  const courseGutterLabels = primitives.labelPrimitives.filter(
    (label) => label.placement === LabelPlacement.CourseGutter
  )
  const shellCourseListLabels = primitives.labelPrimitives.filter(
    (label) => label.placement === LabelPlacement.ShellCourseList
  )

  const courseGutterWidth = Math.max(
    0,
    ...courseGutterLabels.map(
      (label) =>
        measureLabelDimensions(ctx, label).width + LABEL_EDGE_PADDING_PX * 2
    )
  )
  const shellCourseListWidth = Math.max(
    0,
    ...shellCourseListLabels.map(
      (label) =>
        measurePlainLabelDimensions(ctx, label).width +
        LABEL_EDGE_PADDING_PX * 2
    )
  )

  return Math.ceil(Math.max(courseGutterWidth, shellCourseListWidth))
}

function shouldReserveRightGutter(
  request: Pick<
    ReportHeatmapRequest,
    'componentModel' | 'includeOverlay' | 'overlayMode' | 'labelMode'
  >
) {
  if (
    !request.includeOverlay ||
    request.overlayMode !== OverlayMode.Embedded ||
    request.labelMode === LabelMode.None
  ) {
    return false
  }

  return (
    request.componentModel.type === GeckoComponentModelType.VerticalTankShell ||
    request.componentModel.type === GeckoComponentModelType.LegacyCone ||
    request.componentModel.type === GeckoComponentModelType.Cone ||
    request.componentModel.type === GeckoComponentModelType.TankFlatRoof
  )
}

export function renderReportOverlayCanvas(
  ctx: CanvasRenderingContext2D,
  primitives: ReturnType<typeof buildReportOverlayPrimitives>,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  ctx.save()
  ctx.beginPath()
  ctx.rect(
    plotMetrics.left,
    plotMetrics.top,
    plotMetrics.width,
    plotMetrics.height
  )
  ctx.clip()

  primitives.linePrimitives.forEach((line) => {
    ctx.strokeStyle = line.strokeStyle
    ctx.lineWidth = line.lineWidth
    ctx.beginPath()
    ctx.moveTo(
      projectX(line.x1, bounds, plotMetrics),
      projectY(line.y1, bounds, plotMetrics)
    )
    ctx.lineTo(
      projectX(line.x2, bounds, plotMetrics),
      projectY(line.y2, bounds, plotMetrics)
    )
    ctx.stroke()
  })

  primitives.shapePrimitives.forEach((shape) => {
    drawShapePrimitive(ctx, shape, bounds, plotMetrics)
  })

  ctx.restore()

  const anchoredLabels = primitives.labelPrimitives.filter(
    (label) => label.placement === LabelPlacement.Anchored
  )
  const courseGutterLabels = primitives.labelPrimitives.filter(
    (label) => label.placement === LabelPlacement.CourseGutter
  )
  const shellCourseListLabels = primitives.labelPrimitives.filter(
    (label) => label.placement === LabelPlacement.ShellCourseList
  )

  ;[...anchoredLabels]
    .sort((left, right) => left.priority - right.priority)
    .forEach((label) => {
      const labelBox = measureLabelBox(ctx, label, bounds, plotMetrics)
      drawLabelBox(ctx, label, labelBox)
    })

  layoutCourseGutterLabels(
    ctx,
    courseGutterLabels,
    bounds,
    plotMetrics
  ).forEach(({ label, box }) => {
    drawLeaderLine(ctx, label, box, bounds, plotMetrics)
    drawLabelBox(ctx, label, box)
  })

  layoutShellCourseListLabels(
    ctx,
    shellCourseListLabels,
    bounds,
    plotMetrics
  ).forEach(({ label, point, font, lineHeight }) => {
    drawPlainLabel(ctx, label, point.x, point.y, font, lineHeight)
    drawPlainLeaderLine(ctx, label, point, bounds, plotMetrics, lineHeight)
  })
}

function measureLabelBox(
  ctx: CanvasRenderingContext2D,
  label: OverlayLabelPrimitive,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const { width, height } = measureLabelDimensions(ctx, label)
  const x = projectX(label.x, bounds, plotMetrics)
  const y = projectY(label.y, bounds, plotMetrics)

  const box = resolveLabelBox(label.anchor, x, y, width, height)
  const minLeft = plotMetrics.left + LABEL_EDGE_PADDING_PX
  const maxRight = plotMetrics.right - LABEL_EDGE_PADDING_PX
  const minTop = plotMetrics.top + LABEL_EDGE_PADDING_PX
  const maxBottom = plotMetrics.bottom - LABEL_EDGE_PADDING_PX

  return {
    ...box,
    left: clamp(box.left, minLeft, Math.max(minLeft, maxRight - box.width)),
    top: clamp(box.top, minTop, Math.max(minTop, maxBottom - box.height)),
  }
}

function layoutCourseGutterLabels(
  ctx: CanvasRenderingContext2D,
  labels: OverlayLabelPrimitive[],
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  if (labels.length === 0) {
    return [] as PositionedLabel[]
  }

  const minTop = plotMetrics.top + LABEL_EDGE_PADDING_PX
  const maxBottom = plotMetrics.bottom - LABEL_EDGE_PADDING_PX
  const gutterLeft = plotMetrics.gutterLeft + LABEL_EDGE_PADDING_PX
  const gutterRight = plotMetrics.gutterRight - LABEL_EDGE_PADDING_PX

  return [...labels]
    .sort((left, right) => left.priority - right.priority || right.y - left.y)
    .map((label) => {
      const dimensions = measureLabelDimensions(ctx, label)
      const slotTop =
        label.slotStartY === undefined || label.slotEndY === undefined
          ? projectY(label.y, bounds, plotMetrics) - dimensions.height / 2
          : Math.min(
              projectY(label.slotStartY, bounds, plotMetrics),
              projectY(label.slotEndY, bounds, plotMetrics)
            )
      const slotBottom =
        label.slotStartY === undefined || label.slotEndY === undefined
          ? projectY(label.y, bounds, plotMetrics) + dimensions.height / 2
          : Math.max(
              projectY(label.slotStartY, bounds, plotMetrics),
              projectY(label.slotEndY, bounds, plotMetrics)
            )
      const slotCenter = (slotTop + slotBottom) / 2
      const top = clamp(
        slotCenter - dimensions.height / 2,
        minTop,
        Math.max(minTop, maxBottom - dimensions.height)
      )

      return {
        label,
        box: {
          left: Math.max(gutterLeft, gutterRight - dimensions.width),
          top,
          width: dimensions.width,
          height: dimensions.height,
        },
      }
    })
}

function drawLeaderLine(
  ctx: CanvasRenderingContext2D,
  label: OverlayLabelPrimitive,
  box: LabelBox,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const targetX = plotMetrics.right
  const targetY =
    label.slotStartY === undefined || label.slotEndY === undefined
      ? projectY(label.y, bounds, plotMetrics)
      : (projectY(label.slotStartY, bounds, plotMetrics) +
          projectY(label.slotEndY, bounds, plotMetrics)) /
        2
  const labelMidY = box.top + box.height / 2

  ctx.strokeStyle = 'rgba(64, 64, 64, 0.55)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(box.left, labelMidY)
  ctx.lineTo(Math.max(targetX, box.left - 12), labelMidY)
  ctx.lineTo(targetX, targetY)
  ctx.stroke()
}

function layoutShellCourseListLabels(
  ctx: CanvasRenderingContext2D,
  labels: OverlayLabelPrimitive[],
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  if (labels.length === 0) {
    return [] as PositionedTextLabel[]
  }

  const left = plotMetrics.gutterLeft + 10
  const orderedLabels = [...labels].sort((leftLabel, rightLabel) => {
    if (rightLabel.y !== leftLabel.y) {
      return rightLabel.y - leftLabel.y
    }

    return leftLabel.lines[0]?.localeCompare(rightLabel.lines[0] ?? '') ?? 0
  })

  return orderedLabels.map((label) => {
    const slotTop =
      label.slotStartY === undefined || label.slotEndY === undefined
        ? projectY(label.y, bounds, plotMetrics)
        : Math.min(
            projectY(label.slotStartY, bounds, plotMetrics),
            projectY(label.slotEndY, bounds, plotMetrics)
          )
    const slotBottom =
      label.slotStartY === undefined || label.slotEndY === undefined
        ? projectY(label.y, bounds, plotMetrics)
        : Math.max(
            projectY(label.slotStartY, bounds, plotMetrics),
            projectY(label.slotEndY, bounds, plotMetrics)
          )
    const slotHeight = Math.max(0, slotBottom - slotTop)
    const typography = fitShellCourseLabelTypography(label, slotHeight)
    const dimension = measurePlainLabelDimensions(
      ctx,
      label,
      typography.font,
      typography.lineHeight
    )
    const slotCenter = (slotTop + slotBottom) / 2

    return {
      label,
      point: {
        x: left,
        y: slotCenter - dimension.height / 2,
      },
      font: typography.font,
      lineHeight: typography.lineHeight,
    }
  })
}

function drawShapePrimitive(
  ctx: CanvasRenderingContext2D,
  shape: OverlayShapePrimitive,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  ctx.strokeStyle = shape.strokeStyle
  ctx.lineWidth = shape.lineWidth

  switch (shape.kind) {
    case 'circle':
      drawCircleShape(ctx, shape, bounds, plotMetrics)
      break
    case 'rectangle':
      drawRectangleShape(ctx, shape, bounds, plotMetrics)
      break
  }
}

function drawCircleShape(
  ctx: CanvasRenderingContext2D,
  shape: Extract<OverlayShapePrimitive, { kind: 'circle' }>,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const x = projectX(shape.x, bounds, plotMetrics)
  const y = projectY(shape.y, bounds, plotMetrics)
  const radiusX = Math.abs(
    projectX(shape.x + shape.radiusX, bounds, plotMetrics) - x
  )
  const radiusY = Math.abs(
    projectY(shape.y + shape.radiusY, bounds, plotMetrics) - y
  )

  ctx.beginPath()
  ctx.ellipse(x, y, radiusX, radiusY, 0, 0, Math.PI * 2)
  ctx.stroke()
}

function drawRectangleShape(
  ctx: CanvasRenderingContext2D,
  shape: Extract<OverlayShapePrimitive, { kind: 'rectangle' }>,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const left = projectX(shape.x - shape.width / 2, bounds, plotMetrics)
  const right = projectX(shape.x + shape.width / 2, bounds, plotMetrics)
  const top = projectY(shape.y + shape.height / 2, bounds, plotMetrics)
  const bottom = projectY(shape.y - shape.height / 2, bounds, plotMetrics)

  ctx.strokeRect(left, top, right - left, bottom - top)
}

function measureLabelDimensions(
  ctx: CanvasRenderingContext2D,
  label: OverlayLabelPrimitive
) {
  ctx.font = label.font
  const contentWidth = Math.max(
    ...label.lines.map((line) => measureCanvasTextWidth(ctx, line)),
    0
  )

  return {
    width: contentWidth + 16,
    height: label.lines.length * label.lineHeight + 12,
  }
}

function measurePlainLabelDimensions(
  ctx: CanvasRenderingContext2D,
  label: OverlayLabelPrimitive,
  font = label.font,
  lineHeight = label.lineHeight
) {
  ctx.font = font
  const width = Math.max(
    ...label.lines.map((line) => measureCanvasTextWidth(ctx, line)),
    0
  )

  return {
    width,
    height: label.lines.length * lineHeight,
  }
}

function fitShellCourseLabelTypography(
  label: OverlayLabelPrimitive,
  slotHeight: number
) {
  const match = label.font.match(/(\d+(?:\.\d+)?)px/)
  if (!match) {
    return {
      font: label.font,
      lineHeight: label.lineHeight,
    }
  }

  const baseFontSize = Number.parseFloat(match[1])
  const baseHeight = label.lines.length * label.lineHeight
  const targetHeight = Math.max(8, slotHeight - 2)
  const scale = baseHeight <= 0 ? 1 : Math.min(1, targetHeight / baseHeight)
  const scaledFontSize = roundToDecimal(Math.max(12, baseFontSize * scale), 1)
  const scaledLineHeight = Math.max(
    scaledFontSize,
    roundToDecimal(label.lineHeight * scale, 1)
  )

  return {
    font: label.font.replace(match[0], `${scaledFontSize}px`),
    lineHeight: scaledLineHeight,
  }
}

function drawPlainLeaderLine(
  ctx: CanvasRenderingContext2D,
  label: OverlayLabelPrimitive,
  point: PositionedTextLabel['point'],
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics,
  lineHeight: number
) {
  const targetY =
    label.slotStartY === undefined || label.slotEndY === undefined
      ? projectY(label.y, bounds, plotMetrics)
      : (projectY(label.slotStartY, bounds, plotMetrics) +
          projectY(label.slotEndY, bounds, plotMetrics)) /
        2
  const labelMidY = point.y + (label.lines.length * lineHeight) / 2

  ctx.strokeStyle = 'rgba(64, 64, 64, 0.4)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(plotMetrics.right, targetY)
  ctx.lineTo(point.x - 8, labelMidY)
  ctx.stroke()
}

function drawLabelBox(
  ctx: CanvasRenderingContext2D,
  label: OverlayLabelPrimitive,
  labelBox: LabelBox
) {
  ctx.fillStyle = LABEL_BACKGROUND
  ctx.fillRect(labelBox.left, labelBox.top, labelBox.width, labelBox.height)
  ctx.strokeStyle = 'rgba(48, 48, 48, 0.35)'
  ctx.strokeRect(labelBox.left, labelBox.top, labelBox.width, labelBox.height)

  ctx.font = label.font
  ctx.fillStyle = LABEL_TEXT_COLOR
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'

  label.lines.forEach((line, index) => {
    ctx.fillText(
      line,
      labelBox.left + 8,
      labelBox.top + 6 + index * label.lineHeight
    )
  })
}

function drawPlainLabel(
  ctx: CanvasRenderingContext2D,
  label: OverlayLabelPrimitive,
  x: number,
  y: number,
  font = label.font,
  lineHeight = label.lineHeight
) {
  ctx.font = font
  ctx.fillStyle = LABEL_TEXT_COLOR
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'

  label.lines.forEach((line, index) => {
    ctx.fillText(line, x, y + index * lineHeight)
  })
}

function resolveLabelBox(
  anchor: OverlayLabelAnchor,
  x: number,
  y: number,
  width: number,
  height: number
): LabelBox {
  switch (anchor) {
    case LabelAnchor.Top:
      return { left: x - width / 2, top: y, width, height }
    case LabelAnchor.Bottom:
      return { left: x - width / 2, top: y - height, width, height }
    case LabelAnchor.Left:
      return { left: x, top: y - height / 2, width, height }
    case LabelAnchor.Right:
      return { left: x - width, top: y - height / 2, width, height }
    default:
      return { left: x - width / 2, top: y - height / 2, width, height }
  }
}

function projectX(
  value: number,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const start = bounds.rect.x.min.toNumber(bounds.rect.x.unit)
  const end = bounds.rect.x.max.toNumber(bounds.rect.x.unit)
  const ratio = end === start ? 0 : (value - start) / (end - start)
  return plotMetrics.left + ratio * plotMetrics.width
}

function projectY(
  value: number,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const start = bounds.rect.y.min.toNumber(bounds.rect.y.unit)
  const end = bounds.rect.y.max.toNumber(bounds.rect.y.unit)
  const ratio = end === start ? 0 : (value - start) / (end - start)
  return plotMetrics.bottom - ratio * plotMetrics.height
}
